import { Button, Result } from 'antd';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
];

const CHUNK_RELOAD_STORAGE_KEY = 'mpms:chunk-reload';
const CHUNK_RELOAD_MAX_ATTEMPTS = 2;
const CHUNK_RELOAD_WINDOW_MS = 60_000;

interface ChunkReloadState {
  count: number;
  firstAt: number;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));
}

function readChunkReloadState(): ChunkReloadState {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (!raw) return { count: 0, firstAt: 0 };
    const parsed = JSON.parse(raw) as ChunkReloadState;
    if (
      typeof parsed.count !== 'number' ||
      typeof parsed.firstAt !== 'number' ||
      Date.now() - parsed.firstAt > CHUNK_RELOAD_WINDOW_MS
    ) {
      return { count: 0, firstAt: 0 };
    }
    return parsed;
  } catch {
    return { count: 0, firstAt: 0 };
  }
}

function writeChunkReloadState(state: ChunkReloadState): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable — skip persistence
  }
}

function clearChunkReloadState(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — skip
  }
}

export default function RootErrorBoundary() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const reloadState = chunkError ? readChunkReloadState() : { count: 0, firstAt: 0 };
  const canRetry = chunkError && reloadState.count < CHUNK_RELOAD_MAX_ATTEMPTS;

  useEffect(() => {
    if (!chunkError || !canRetry) return;
    writeChunkReloadState({
      count: reloadState.count + 1,
      firstAt: reloadState.firstAt || Date.now(),
    });
    window.location.reload();
  }, [chunkError, canRetry, reloadState.count, reloadState.firstAt]);

  if (chunkError && canRetry) {
    return <Result status="info" title="페이지를 다시 불러오는 중입니다." />;
  }

  return (
    <Result
      status="error"
      title={chunkError ? '페이지를 불러오지 못했습니다' : '문제가 발생했습니다'}
      subTitle={
        chunkError
          ? '네트워크 상태를 확인한 뒤 다시 시도해 주세요. 문제가 계속되면 관리자에게 문의해 주세요.'
          : '페이지를 새로고침 후에도 문제가 계속되면 관리자에게 문의해 주세요.'
      }
      extra={
        <Button
          type="primary"
          onClick={() => {
            clearChunkReloadState();
            window.location.reload();
          }}
        >
          새로고침
        </Button>
      }
    />
  );
}
